import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData, getNoSlip } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import { showPopupModal } from "@widgets/CommonModal";
import SN_SAL_ISU_REG_005_POP_001 from "@menu/SAL/SN_SAL_ISU_REG_005/SN_SAL_ISU_REG_005_POP_001";
import SnButton from "@shared/components/SnButton";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import { dateToString } from "@shared/utils/DateUtils";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

interface Props {
  Menuprops: menuInfo;
}
interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

// 출하반품정리등록
const SN_SAL_ISU_REG_005 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login userInfo 정보
  //#endregion
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [gridFocus, setGridFocus] = useState(false);
  //#endregion

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_SAN_ISU_REG_005";
  const defaultSize = { panel1Height: "45.5%", panel2Height: "45.5%" };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);

  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedDtDocu, setSelectedDtDocu] = useState<string>("");

  //#region  Button Event
  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();

        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(rtn.formData); // 조회조건 초기값
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    gridRefs.current["grid1"].current.selectAndFocusRow(0);
    setGridFocus(false);
  }, [gridFocus]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    gridRefs.current["grid1"].current.selectAndFocusRow(0);
    setGridFocus(true);
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
      }
    }
    try {
      const param = {
        id: "sn_sal_isu_reg_005_01",
        params: searchRef.current.getFormdata(),
      };
      const data: any = await fnGetDataSet(param);
      setDataSources({
        grid1: data[0] || [],
        grid2: data[1] || [],
      });

      if (data[0] && data[0].length > 0) {
        // 1. 그리드 데이터가 바인딩되었음을 기다림
        await new Promise((resolve) => setTimeout(resolve, 50)); // 비동기 반영 대기 (최적화 가능)

        // 2. 첫 번째 행 선택 및 포커스 설정
        gridRefs.current["grid1"].current.selectAndFocusRow(0);
      }

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onInsert = async () => {
    try {
      const newRow = {}; // 빈 객체
      gridRefs.current["grid1"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onBeforeAddRowH = () => {
    try {
      const mtlQtiohChanges =
        gridRefs.current["grid1"].current.getChange().insert;

      if (mtlQtiohChanges.length > 0) return false;
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        cd_dept: userInfo?.cd_dept,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_io: today,
        fg_trns: "1",
        cd_gp: userInfo?.cd_gp,
        nm_gp: userInfo?.nm_gp,
        nm_dept: userInfo?.nm_dept,
        cd_exch: "000",
        rt_exch: 1,
        cd_cust_dlv: "",
        yn_autoprocess: "N",
        st_req: "F",
        fg_io: "1",
        no_hst: "0",
        fg_money: "1",
        no_io: "",
        fg_map: "1",
        fg_slip: "2",
        tp_slip: "2",
        cd_io: "250",
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        qt_dummy01: 0,
        qt_dummy02: 0,
      };
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onAddRow = async () => {
    try {
      const newRow = {}; // 빈 객체
      gridRefs.current["grid2"].current?.addRow(newRow);
    } catch (error: any) {}
  };

  const onBeforeAddRowL = () => {
    try {
      // gridH가 선택되지 않았을 경우 리턴
      if (
        !gridRefs.current["grid1"].current ||
        gridRefs.current["grid1"].current.getSelectedRows().length === 0
      ) {
        alert("선택된 행이 없습니다.");
        return false;
      }
      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }
      const allRowData: any[] = [];
      gridRefs.current["grid2"].current.forEachFilteredNode((node: any) => {
        allRowData.push(node.data);
      });

      const selectedRows = gridRefs.current["grid1"].current.getSelectedRows();
      const maxNoLine =
        allRowData.length > 0
          ? Math.max(...allRowData.map((row: any) => row.no_line || 0))
          : 0;
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        no_line: maxNoLine + 1,
        no_line_lc: maxNoLine + 1,
        no_line_req: maxNoLine + 1,
        cd_biz_isu: userInfo?.cd_biz,
        dt_dlv: today,
        no_io: selectedRows[0].data?.no_io,
        cd_io: selectedRows[0].data?.cd_io,
        st_req: selectedRows[0].data?.st_req,
        fg_slip: selectedRows[0].data?.fg_slip,
        tp_slip: selectedRows[0].data?.tp_slip,
        fg_io: selectedRows[0].data?.fg_io,
        fg_money: selectedRows[0].data?.fg_money,
        fg_map: selectedRows[0].data?.fg_map,
        rt_exch: selectedRows[0].data?.rt_exch,
        yn_autoprocess: selectedRows[0].data?.yn_autoprocess,
        dt_io: today,
        cd_dept: userInfo?.cd_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        fg_trns: "1",
        fg_vat: "11",
        no_line_order: maxNoLine + 1,
        no_line_src_lot: "0",
        fg_req: "100",
        yn_req: "Y",
        yn_insp_report: "N",
        qt: 0,
        qt_isu: 0,
        qt_inv: 0,
        qt_ep: 0,
        qt_cls: 0,
        qt_mgmt: 0,
        um: 0,
        am: 0,
        am_k: 0,
        qt_dummy01: 0,
        qt_dummy02: 0,
        qt_dummy03: 0,
        um_k_pack: 0,
        um_supply_pack: 0,
        qt_isu_pack: 0,
        qt_cls_pack: 0,
        qt_dir: 0,
        rt_fat: 0,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() === 0) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    await onSave();
  };

  const onDeleteRow = async () => {
    gridRefs.current["grid2"].current?.deleteSelectedRows();
    const footerData = gridRefs.current["grid2"].current.getFooterData();

    // gridApiRefH에서 focusedCell 찾기 및 setDataValue 작업
    const gridApiH = gridRefs.current["grid1"].current;

    // 공통 메서드로 footerData 값을 focusedRow에 적용
    await setFooterDataToRow(gridApiH, footerData);
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return;
      }

      // L 필수값 체크
      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return;
      }

      const mtlQtiohChanges = gridRefs.current["grid1"].current.getChange();
      const mtlQtiohanges = gridRefs.current["grid2"].current.getChange();
      const salIsureqhChanges = mtlQtiohChanges;
      const salIsureqlChanges = mtlQtiohanges;
      const headerInserts = mtlQtiohChanges.insert ?? [];
      const lineInserts = mtlQtiohanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog("alter", "저장에 실패했습니다.");
        return;
      }

      if (
        mtlQtiohChanges.modifiedRowcount === 0 &&
        mtlQtiohanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return;
      }

      //신규 등록된 수주건이 있는 경우 채번
      for (let row of headerInserts) {
        if (!row.no_req) {
          const cd_module: any = Menuprops.cd_module;
          try {
            // getNoSlip 호출
            const no_slip = await getNoSlip(
              cd_module,
              10, // no_line 값이 필요한 경우 사용
              "IXT", // cd_prefix 값이 필요하다면 여기에 적어주세요.
              row.dt_req, // 해당 날짜를 넘겨줘야 할 경우
              Menuprops.cd_menu || "SN_SAL_ISU_REG_005"
            );

            if (no_slip) {
              row.no_io = no_slip;
              row.no_req = no_slip;

              const gridApiH = gridRefs.current["grid1"].current;
              const focusedCell = gridApiH.focusedRowNode();
              focusedCell.setDataValue("no_io", no_slip);
              focusedCell.setDataValue("no_req", no_slip);

              for (let row of lineInserts) {
                if (!row.no_req) {
                  if (no_slip) {
                    row.no_io = no_slip;
                    row.no_req = no_slip;
                  }
                }
              }
              gridRefs.current["grid2"].current.applyFilters({
                no_io: no_slip,
              });
              gridRefs.current["grid2"].current.applyFilters({
                no_req: no_slip,
              });
            }
          } catch (error) {}
        }
      }

      const SaveData = {
        sal_isureqh: salIsureqhChanges,
        sal_isureql: salIsureqlChanges,
        mtl_qtioh: mtlQtiohChanges,
        mtl_qtio: mtlQtiohanges,
      };
      // 저장 API 호출
      await fnSave("SN_SAL_ISU_REG_005_SAVE_01", SaveData);

      // 성공적으로 저장된 후 초기화
      gridRefs.current["grid1"].current?.resetUpdate();
      gridRefs.current["grid2"].current?.resetUpdate();
    } catch (error: any) {
      gridRefs.current["grid1"].current?.resetUpdate(true);
      gridRefs.current["grid2"].current?.resetUpdate(true);
    }
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id === "addRow") {
      onAddRow();
    }
    if (id === "deleteRow") {
      onDeleteRow();
    }
    if (id == "aButton_app") {
      const mtlQtiohChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (mtlQtiohChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz,
        cd_cust: selectedFormData.cd_cust,
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
        fg_trns: selectedFormData.fg_trns,
      };

      const component = (
        <SN_SAL_ISU_REG_005_POP_001 selectedData={selectedData} />
      );
      const result = await showPopupModal({
        title: "반품적용",
        children: component,
      });
      if (!result || result === "close") return;

      const headerResult = result[0];
      const lineResult: any[] = result[1];

      const focusedCellH = gridRefs.current["grid1"].current.focusedRowNode();
      const today = dateToString(new Date());
      if (focusedCellH) {
        if (headerResult[0]) {
          headerResult[0].cd_firm &&
            focusedCellH.setDataValue("cd_firm", headerResult[0].cd_firm);
          headerResult[0].cd_biz &&
            focusedCellH.setDataValue("cd_biz", headerResult[0].cd_biz);
          headerResult[0].dt_io && focusedCellH.setDataValue("dt_io", today);
          headerResult[0].dt_sales &&
            focusedCellH.setDataValue("dt_sales", today);
          headerResult[0].cd_cust &&
            focusedCellH.setDataValue("cd_cust", headerResult[0].cd_cust);
          headerResult[0].nm_cust &&
            focusedCellH.setDataValue("nm_cust", headerResult[0].nm_cust);
          headerResult[0].nm_cust_sort &&
            focusedCellH.setDataValue(
              "nm_cust_sort",
              headerResult[0].nm_cust_sort
            );
          headerResult[0].fg_trns &&
            focusedCellH.setDataValue("fg_trns", headerResult[0].fg_trns);
          headerResult[0].cd_exch &&
            focusedCellH.setDataValue("cd_exch", headerResult[0].cd_exch);
          headerResult[0].cd_dept &&
            focusedCellH.setDataValue("cd_dept", headerResult[0].cd_dept);
          headerResult[0].cd_gp &&
            focusedCellH.setDataValue("cd_gp", headerResult[0].cd_gp);
          headerResult[0].cd_emp &&
            focusedCellH.setDataValue("cd_emp", headerResult[0].cd_emp);
          headerResult[0].cd_wh &&
            focusedCellH.setDataValue("cd_wh", headerResult[0].cd_wh);
          headerResult[0].cd_gp &&
            focusedCellH.setDataValue("cd_gp", headerResult[0].cd_gp);
          headerResult[0].fg_slip &&
            focusedCellH.setDataValue("fg_slip", headerResult[0].fg_slip);
          headerResult[0].tp_slip &&
            focusedCellH.setDataValue("tp_slip", headerResult[0].tp_slip);
          headerResult[0].fg_io && focusedCellH.setDataValue("fg_io", "3");
          headerResult[0].fg_money &&
            focusedCellH.setDataValue("fg_money", headerResult[0].fg_money);
          focusedCellH.setDataValue("dt_req", today);
          focusedCellH.setDataValue("cd_biz_isu", headerResult[0].cd_biz);
          headerResult[0].st_req && focusedCellH.setDataValue("st_req", "F");
          headerResult[0].rt_exch &&
            focusedCellH.setDataValue("rt_exch", headerResult[0].rt_exch);
          headerResult[0].cd_io && focusedCellH.setDataValue("cd_io", "250");
          headerResult[0].cd_pjt &&
            focusedCellH.setDataValue("cd_pjt", headerResult[0].cd_pjt);
          headerResult[0].cd_mgmtdtl &&
            focusedCellH.setDataValue("cd_mgmtdtl", headerResult[0].cd_mgmtdtl);
        }
      }

      lineResult.map((result: any) => {
        if (headerResult[0]) {
          onAddRow();
          const rowCount =
            gridRefs.current["grid2"].current.getDisplayedRowCount();
          const lastRowIndex = rowCount - 1;

          const lastRowNode =
            gridRefs.current["grid2"].current.getRowNodeAtIndex(lastRowIndex);

          if (lastRowNode) {
            lastRowNode.setDataValue("cd_firm", result.cd_firm);
            lastRowNode.setDataValue("fg_vat", result.fg_vat);
            lastRowNode.setDataValue("rt_exch", result.rt_exch);
            lastRowNode.setDataValue("no_so", result.no_order);
            lastRowNode.setDataValue("no_line_so", result.no_line_order);
            lastRowNode.setDataValue("no_line_req", result.no_line);
            lastRowNode.setDataValue("no_line", result.no_line);
            lastRowNode.setDataValue("no_order", result.no_order);
            lastRowNode.setDataValue("no_line_order", result.no_line_order);
            lastRowNode.setDataValue("no_isu_src", result.no_io);
            lastRowNode.setDataValue("no_line_lc", result.no_line_lc);
            lastRowNode.setDataValue("no_line_isu_src", result.no_line);
            lastRowNode.setDataValue("dt_req", today);
            lastRowNode.setDataValue("cd_item", result.cd_item);
            lastRowNode.setDataValue("nm_item", result.nm_item);
            lastRowNode.setDataValue("dc_item_spec", result.dc_item_spec);
            lastRowNode.setDataValue("dc_item_unit", result.dc_item_unit);
            lastRowNode.setDataValue("um", result.um);
            lastRowNode.setDataValue("um_k", result.um_k);
            lastRowNode.setDataValue("am", result.am);
            lastRowNode.setDataValue("am_k", result.am_k);
            lastRowNode.setDataValue("am_vat", result.am_vat);
            lastRowNode.setDataValue("am_supply", result.am_supply);
            lastRowNode.setDataValue("um_supply", result.um_supply);
            lastRowNode.setDataValue("qt", result.qt_app);
            lastRowNode.setDataValue("qt_good", result.qt_app);
            lastRowNode.setDataValue("cd_pjt", result.cd_pjt);
            lastRowNode.setDataValue("dc_rmk", result.dc_rmk);
            lastRowNode.setDataValue("cd_wh", result.cd_wh);
            lastRowNode.setDataValue("nm_wh", result.nm_wh);
            lastRowNode.setDataValue("yn_lot", result.yn_lot);
            lastRowNode.setDataValue("fg_slip", result.fg_slip);
            lastRowNode.setDataValue("tp_slip", result.tp_slip);
            lastRowNode.setDataValue("fg_io", "3");
            lastRowNode.setDataValue("fg_money", result.fg_money);
            lastRowNode.setDataValue("dt_req", today);
            lastRowNode.setDataValue("dt_io", result.dt_io);
            lastRowNode.setDataValue("cd_biz", result.cd_biz);
            lastRowNode.setDataValue("cd_biz_isu", result.cd_biz);
            lastRowNode.setDataValue("st_req", "F");
            lastRowNode.setDataValue("cd_io", "250");
          }
        }
      });
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      // 배열로 만들어서 필터링 함수 호출
      const no_io = selectedRow.no_io;
      const dt_io = selectedRow.dt_io || "";
      gridRefs.current["grid2"].current.applyFilters({ no_io: no_io });
      gridRefs.current["grid2"].current.selectAndFocusRow(0);
      setSelectedDocu(no_io);
      setSelectedDtDocu(dt_io);
    } else {
      return;
    }
  };

  // 계산식 함수
  const calculateAmounts = (row: any) => {
    const qt = row.qt ?? 0;
    const um = row.um ?? 0;
    row.am = qt * um;
    row.am_k = row.am;
    row.am_vat = row.am * 0.1;
    row.am_supply = row.am + row.am_vat;
  };

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;
    const gridApiH = gridRefs.current["grid1"].current;
    if (gridApiH) {
      const selectedDataH = gridApiH.getSelectedRows();
      if (selectedDataH.length > 0) {
        selectedDataH.forEach((row: any) => {
          if (row.cd_cust !== row._previousCdCust) {
            row.cd_cust_dlv = row.cd_cust; // cd_cust_dlv에 값을 넣음
          }
        });
      }
    }
  };

  const onCellValueChanged = async (event: any) => {
    if (!event) return;

    const updatedRow = event.rowData;
    const updatedColumnId = event.colId;

    if (updatedColumnId === "qt" || updatedColumnId === "um") {
      calculateAmounts(updatedRow);
    }

    const footerData = gridRefs.current["grid2"].current.getFooterData();

    const gridApiH = gridRefs.current["grid1"].current;
    await setFooterDataToRow(gridApiH, footerData);
  };

  // footer에 집계된 내용 상단 그리드에 적용
  const setFooterDataToRow = async (gridApi: any, footerData: any) => {
    const focusedCell = gridApi.focusedRowNode();

    if (focusedCell) {
      focusedCell.setDataValue("am", footerData.am ?? 0);
      focusedCell.setDataValue("am_k", footerData.am_k ?? 0);
      focusedCell.setDataValue("am_vat", footerData.am_vat ?? 0);
      focusedCell.setDataValue("am_supply", footerData.am_supply ?? 0);
    }
  };
  //#endregion
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
          onCellValueChanged: onCellValueChangedH,
          onBeforeAddRow: onBeforeAddRowH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChanged,
          onBeforeAddRow: onBeforeAddRowL,
          onBtnAddClick: onGrid1BtnsClick,
          onBtnDeleteClick: onGrid1BtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizable
          direction={"height"}
          minHeight={130}
          onResizeStop={onResizeStop}
          enable={{ bottom: true }}
          seq={1}
          size={sizes.panel1Height}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
        >
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{
              isEditable: true,
              rowSelection: "single",
              title: "반품정리정보",
            }}
            events={grids.grid1.events}
            header={
              <div className="flex justify-end">
                <SnButton
                  text={"반품적용"}
                  onClick={() => {
                    onGrid1BtnsClick("aButton_app");
                  }}
                />
              </div>
            }
          />
        </SnResizable>
        <SnResizable
          direction={"height"}
          minHeight={130}
          onResizeStop={onResizeStop}
          enable={{ bottom: true }}
          seq={2}
          size={sizes.panel2Height}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
        >
          <SnGrid
            ref={gridRefs.current["grid2"]} // Grid Name
            columnConfig={grids.grid2.columns} // 컬럼 설정 전달
            rowData={grids.grid2.dataSource} // Datasource
            etcProps={{
              isEditable: true,
              rowSelection: "single",
              title: "상세정보",
            }}
            events={grids.grid2.events}
          />
        </SnResizable>
        <SnFileUploadBox
          selectedDocu={selectedDocu}
          selectedLineDocu={0}
          selectedDtDocu={selectedDtDocu}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_SAL_ISU_REG_005;
